import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * `attention` = the request is waiting on the reader to do something.
 * `neutral` = something happened they should know about but cannot act on.
 *
 * Two tones on purpose: if a card that needs nothing from the consumer looks
 * the same as one that blocks their trámite, neither reads as urgent.
 */
export type RequestAlertTone = "attention" | "neutral";

export interface RequestAlertCardProps {
  tone: RequestAlertTone;
  icon: LucideIcon;
  title: string;
  children: ReactNode;
  /** Right-aligned action row under the body. */
  action?: ReactNode;
  className?: string;
}

const TONE: Record<RequestAlertTone, { card: string; rail: string; iconWrap: string; icon: string }> =
  {
    attention: {
      card: "border-amber-200/80 dark:border-amber-900/50",
      rail: "border-l-amber-500 dark:border-l-amber-400",
      iconWrap: "bg-amber-100 dark:bg-amber-950/60",
      icon: "text-amber-800 dark:text-amber-300",
    },
    neutral: {
      card: "border-border",
      rail: "border-l-muted-foreground/60",
      iconWrap: "bg-muted",
      icon: "text-muted-foreground",
    },
  };

/**
 * The "this request needs your attention" card: an accent rail, a round icon,
 * a title, and whatever the situation needs in the body.
 *
 * Extracted because it already existed twice, written out by hand, in the
 * consumer request detail — `missing_information` and `cannot_complete` — and
 * a third surface needed it. Same action, same visual style: a professional
 * asking the consumer a question on an RFQ is the same kind of event as a
 * professional asking for a missing document, and the consumer should not
 * have to learn two visual languages for it.
 */
export function RequestAlertCard({
  tone,
  icon: Icon,
  title,
  children,
  action,
  className,
}: RequestAlertCardProps): JSX.Element {
  const styles = TONE[tone];
  return (
    <Card
      className={cn("overflow-hidden border shadow-sm transition-colors duration-150", styles.card, className)}
    >
      <CardContent className="p-0">
        <div className={cn("flex gap-0 border-l-4", styles.rail)}>
          <div className="min-w-0 flex-1 space-y-3 p-4">
            <div className="flex items-start gap-3">
              <div
                className={cn(
                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                  styles.iconWrap,
                )}
                aria-hidden
              >
                <Icon className={cn("h-5 w-5", styles.icon)} />
              </div>
              <div className="min-w-0 flex-1 space-y-2">
                <h3 className="text-base font-semibold text-foreground">{title}</h3>
                {children}
              </div>
            </div>
            {action ? <div className="flex w-full justify-end">{action}</div> : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
